import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { ArrowDown, ArrowRight, Pause, Play, RotateCcw } from "lucide-react";
import ArchitectureFallback from "@/components/ArchitectureFallback";
import ArchitectureDescription from "@/components/ArchitectureDescription";
import { LAYERS, SCENES, TRACK_SVH, beatAnchor, sceneAnchor } from "@/components/architectureStory";
import "./ArchitectureHero.css";

function CanvasUnavailable({ onStatusChange }: { onStatusChange: (ready: boolean) => void }) {
  useEffect(() => onStatusChange(false), [onStatusChange]);
  return <></>;
}
const ArchitectureCanvas = lazy(() => import("@/components/ArchitectureCanvas")
  .catch(() => ({ default: CanvasUnavailable })));
const prefersReducedMotion = () => typeof window !== "undefined"
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

interface ArchitectureHeroProps {
  onExploreArchitecture: () => void;
  onSeeDataFlow: () => void;
}

export default function ArchitectureHero({ onExploreArchitecture, onSeeDataFlow }: ArchitectureHeroProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLDivElement>(null);
  const [story, setStory] = useState({ scene: 0, beat: -1 });
  const [webglStatus, setWebglStatus] = useState<"loading" | "ready" | "fallback">("loading");
  const [reduced, setReduced] = useState(prefersReducedMotion);
  const [autoPlay, setAutoPlay] = useState(() => !prefersReducedMotion());
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [introComplete, setIntroComplete] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(() => window.innerWidth < 640 ? 107 : 77);

  useEffect(() => {
    const boot = document.getElementById("architecture-boot");
    // The HTML poster survives routing and font loading, then gives way at the
    // same geometry. Keep the renderer's clock held until this handoff.
    let cancelled = false;
    document.fonts.ready.then(() => requestAnimationFrame(() => {
      if (!cancelled) boot?.remove();
    }));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => { setReduced(query.matches); if (query.matches) setAutoPlay(false); };
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    const update = () => setPageVisible(!document.hidden);
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    observer.observe(track);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const header = document.querySelector("#root [data-marketing-nav]");
    if (!header) return;
    const observer = new ResizeObserver(() => setHeaderHeight(header.getBoundingClientRect().height));
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!autoPlay || !inView) return;
    const pause = () => setAutoPlay(false);
    const key = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("button, input, textarea, select, a")) return;
      if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", "Home", "End", " "].includes(event.key)) pause();
    };
    window.addEventListener("wheel", pause, { passive: true });
    window.addEventListener("touchmove", pause, { passive: true });
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("wheel", pause);
      window.removeEventListener("touchmove", pause);
      window.removeEventListener("keydown", key);
    };
  }, [autoPlay, inView]);

  const handleStoryChange = useCallback((scene: number, beat: number) => {
    setStory((current) => current.scene === scene && current.beat === beat ? current : { scene, beat });
  }, []);
  const handleStatusChange = useCallback((ready: boolean) => {
    setWebglStatus(ready ? "ready" : "fallback");
    if (!ready) setAutoPlay(false);
  }, []);
  const scrollToFraction = useCallback((fraction: number, automatic = false) => {
    const track = trackRef.current;
    if (!track) return;
    const top = track.getBoundingClientRect().top + window.scrollY - headerHeight;
    const distance = Math.max(0, track.offsetHeight - (window.innerHeight - headerHeight));
    // The pinned stage stays put. Its GSAP timeline already eases the camera;
    // a second browser scroll animation would add a long, unannounced delay.
    window.scrollTo({ top: top + distance * fraction,
      behavior: automatic || prefersReducedMotion() ? "instant" : "smooth" });
  }, [headerHeight]);
  const navigate = (scene: number, beat = -1) => {
    setAutoPlay(false);
    stageRef.current?.dispatchEvent(new Event("ah-navigate"));
    scrollToFraction(beat < 0 ? sceneAnchor(scene) : beatAnchor(scene, beat));
  };
  const skipTour = () => {
    setAutoPlay(false);
    stageRef.current?.dispatchEvent(new Event("ah-navigate"));
    const track = trackRef.current;
    if (!track) return;
    const nextSectionTop = track.getBoundingClientRect().bottom + window.scrollY;
    window.scrollTo({ top: Math.max(0, nextSectionTop - headerHeight + 2), behavior: "instant" });
    const heading = track.closest(".ah-hero")?.nextElementSibling?.querySelector<HTMLElement>("h2");
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
  };

  const { scene: activeScene, beat: activeBeat } = story;
  const currentScene = SCENES[activeScene];
  const currentBeat = currentScene.beats[Math.max(0, activeBeat)];
  const next = activeScene === 0 ? { scene: 1, beat: 0 }
    : activeBeat < currentScene.beats.length - 1 ? { scene: activeScene, beat: activeBeat + 1 }
      : activeScene < SCENES.length - 1 ? { scene: activeScene + 1, beat: 0 } : null;
  const duration = activeScene === 0 ? 2400 : 5200;
  const playing = autoPlay && !reduced && inView && pageVisible && webglStatus === "ready" && Boolean(next);
  const nextScene = next?.scene;
  const nextBeat = next?.beat;
  useEffect(() => {
    if (!playing || !introComplete || nextScene === undefined || nextBeat === undefined) return;
    const timer = window.setTimeout(() => scrollToFraction(beatAnchor(nextScene, nextBeat), true), duration);
    return () => window.clearTimeout(timer);
  }, [playing, introComplete, nextScene, nextBeat, duration, scrollToFraction]);

  const handleIntroComplete = useCallback(() => setIntroComplete(true), []);

  const playbackLabel = reduced ? "Explore at your pace"
    : webglStatus === "loading" ? "Opening the architecture tour"
      : webglStatus === "fallback" ? "Architecture overview"
        : !next ? "Tour complete" : playing ? "Playing the architecture tour" : "Tour paused · scroll to explore";

  return (
    <section className={`ah-hero ${webglStatus === "ready" ? "ah-webgl-ready" : ""}`} aria-labelledby="ah-title" data-renderer={webglStatus} data-intro={introComplete ? "complete" : "playing"}
      style={{ "--ah-header": `${headerHeight}px`, "--ah-track": `${TRACK_SVH}svh` } as CSSProperties}>
      <div className="ah-scroll-track" ref={trackRef}>
        <div className="ah-stage" ref={stageRef}>
          <div className="ah-visual" ref={visualRef} aria-label="Architecture visualization">
            <div className="ah-frame">
            <ArchitectureFallback />
            <Suspense fallback={null}>
              <ArchitectureCanvas trackRef={trackRef} stageRef={stageRef} visualRef={visualRef}
                playing={autoPlay && inView && pageVisible} onIntroComplete={handleIntroComplete}
                onStoryChange={handleStoryChange} onStatusChange={handleStatusChange} />
            </Suspense>
            </div>
            <div className="ah-visual-caption" aria-hidden="true">
              <span>ONTOLOGY / {activeScene === 0 ? "SHARED FOUNDATION" : currentScene.eyebrow.split(" / ")[1]}</span>
              <span>ILLUSTRATIVE ARCHITECTURE</span>
            </div>
          </div>

          <div className="ah-copy" data-active-scene={activeScene} inert={activeScene > 0} aria-hidden={activeScene > 0}>
            <p className="ah-kicker">Enterprise intelligence, connected</p>
            <h1 id="ah-title">Governed intelligence.<br /><em>Built once.</em><br />Reused everywhere.</h1>
            <p className="ah-lede"><ArchitectureDescription key={`welcome-${activeScene === 0}`} active={activeScene === 0} reduced={reduced}
              text="Give every team a shared understanding of your business. Extend it locally. Carry its meaning into every product and decision." /></p>
            <div className="ah-actions">
              {webglStatus === "fallback" ? <a href="#prototypes" className="ah-button ah-button--primary">
                Explore interactive prototypes <ArrowRight size={15} aria-hidden />
              </a> : <>
              <button className="ah-button ah-button--primary" type="button" onClick={() => {
                onExploreArchitecture(); navigate(1);
              }}>Explore the architecture <ArrowRight size={15} aria-hidden /></button>
              <button className="ah-button" type="button" onClick={() => {
                onSeeDataFlow(); navigate(5);
              }}>Follow a record <ArrowDown size={14} aria-hidden /></button>
              </>}
            </div>
            <p className="ah-opening-cue"><span aria-hidden="true" />Shared meaning → local context → traceable outcomes</p>
          </div>

          <div className="ah-caption" data-active-scene={activeScene} aria-hidden={activeScene === 0} inert={activeScene === 0}>
            <p className="ah-kicker">{currentScene.eyebrow}</p>
            <h2>{currentScene.title}</h2>
            <p className="ah-scene-description"><ArchitectureDescription key={activeScene} text={currentScene.description} reduced={reduced} active={activeScene > 0} /></p>
            {currentBeat && <div className="ah-beat-copy" key={`${activeScene}-${activeBeat}`}>
              <span className="ah-beat-label">{String(Math.max(0, activeBeat) + 1).padStart(2, "0")} / {currentBeat.label}</span>
              <p><ArchitectureDescription text={currentBeat.text} reduced={reduced} /></p>
            </div>}
            <ol className="ah-beats" aria-label="Steps in this scene">
              {currentScene.beats.map((beat, index) => <li key={beat.label}>
                <button type="button" className="ah-beat" aria-current={index === activeBeat ? "step" : undefined}
                  data-active={index === activeBeat} onClick={() => navigate(activeScene, index)}>
                  <span className="ah-beat-number">0{index + 1}</span><span>{beat.label}</span><ArrowRight size={13} aria-hidden />
                </button>
              </li>)}
            </ol>
            {currentScene.note && <p className="ah-illustrative">{currentScene.note}</p>}
          </div>

          <ul className="ah-layers" aria-label="Architecture chapters">
            {LAYERS.map((layer) => <li key={layer.id}>
              <button className="ah-layer" type="button" data-active={currentScene.layers.includes(layer.id)} disabled={webglStatus === "fallback"}
                onClick={() => navigate(layer.scene)}>
                <span>{layer.name}</span><small>{layer.hint}</small>
              </button>
            </li>)}
          </ul>

          <div className="ah-player" data-playing={playing}>
            <div className="ah-player-top">
              <span className="ah-player-status"><span className="ah-live-dot" aria-hidden="true" />{playbackLabel}</span>
              <span className="ah-player-controls">
                <button className="ah-autoplay" type="button" aria-pressed={autoPlay && Boolean(next)}
                  disabled={reduced || webglStatus === "fallback"}
                  onClick={() => { if (!next) scrollToFraction(0); setAutoPlay(!next || !autoPlay); }}>
                  {!next ? <RotateCcw size={13} aria-hidden /> : autoPlay ? <Pause size={13} aria-hidden /> : <Play size={13} aria-hidden />}
                  {!next ? "Replay" : autoPlay ? "Pause" : "Play"}
                </button>
                <button className="ah-skip" type="button" onClick={skipTour}
                  aria-label="Skip architecture tour and continue to the next section">
                  Skip tour <ArrowDown size={13} aria-hidden />
                </button>
              </span>
            </div>
            <div className="ah-tour-track" aria-hidden="true">
              <span key={`${activeScene}-${activeBeat}-${playing}`} className={playing ? "ah-tour-fill is-playing" : "ah-tour-fill"}
                style={{ "--beat-duration": `${duration}ms` } as CSSProperties} />
            </div>
            <div className="ah-player-bottom">
              <span>{webglStatus === "loading" ? "Start with a shared foundation" : next
                ? `Up next · ${SCENES[next.scene].beats[next.beat].label}` : "Every outcome keeps its lineage"}</span>
              <nav className="ah-scene-nav" aria-label="Architecture story scenes">
                {SCENES.map((scene, index) => <button type="button" className="ah-scene-dot" key={scene.eyebrow}
                  aria-label={`Go to scene ${index + 1}: ${scene.title}`} aria-current={activeScene === index ? "step" : undefined}
                  data-active={activeScene === index} disabled={webglStatus === "fallback"} onClick={() => navigate(index)} />)}
              </nav>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
